import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import OfferRecommendations from 'components/Common/OfferRecommendations/OfferRecommendations'
import { HOMES_AND_VILLAS_FILTERS } from 'constants/offerList'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferList from 'hooks/Offers/useOfferList'
import { useAppSelector } from 'hooks/reduxHooks'
import useStableObjectReference from 'hooks/useStableObjectReference'
import { EmptyArray, without } from 'lib/array/arrayUtils'
import getOfferListKey from 'lib/offer/offerListKey'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { encodeSearchParams } from 'lib/search/searchUtils'
import {
  arePropertyRatesLoading,
  getPropertyCombinedRatesByRoom,
} from 'selectors/offerPage/offerPageSelectors'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

import type { RoomRateViews } from './singlePropertyUtils'

const NEARBY_SINGLE_UNIT_LIMIT = 10
const NEARBY_SINGLE_UNIT_SEARCH_LIMIT = 12
const HOMES_AND_VILLAS_SEARCH_PATH = '/search/homes-and-villas'

interface Props {
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offerId: string | undefined
  property: App.Property.Property
  checkInDate: string | undefined
  checkOutDate: string | undefined
  rooms: Array<App.Occupants>
  isLoading: boolean
  combinedRatesByRoom: Array<RoomRateViews>
  propertyTypes: Array<string>
}

function PropertyUnavailableSingleUnitRecommendations(props: Props) {
  const {
    offerId,
    property,
    checkInDate,
    checkOutDate,
    rooms,
    isLoading,
    combinedRatesByRoom,
    propertyTypes,
  } = props
  const intl = useIntl()
  const stableRooms = useStableObjectReference(rooms)
  const disabled = useMemo(() => !(
    (property.singleUnit || combinedRatesByRoom.length === 1) &&
    checkInDate &&
    checkOutDate &&
    !isLoading &&
    combinedRatesByRoom.length > 0 &&
    combinedRatesByRoom.every(room => !room.datesAvailable && !room.allRatesQuickFiltered)
  ), [checkInDate, checkOutDate, combinedRatesByRoom, isLoading, property.singleUnit])

  const filters = useMemo<App.OfferListFilters | undefined>(() => {
    if (disabled) return undefined

    return {
      ...HOMES_AND_VILLAS_FILTERS,
      propertyId: property.id,
      ...(propertyTypes?.length ? { propertyTypes } : {}),
      checkIn: checkInDate,
      checkOut: checkOutDate,
      rooms: stableRooms,
      isStream: true,
      include: ['nearby'],
      limit: NEARBY_SINGLE_UNIT_SEARCH_LIMIT,
      showDistanceWithMetaOfferId: offerId,
    }
  }, [checkInDate, checkOutDate, disabled, offerId, property.id, propertyTypes, stableRooms])

  const offerList = useOfferList(filters, {
    disabled: !filters,
    ...(!!offerId && { excludedIds: [offerId] }),
    minRequiredOffers: NEARBY_SINGLE_UNIT_SEARCH_LIMIT,
  })

  const offerListKey = useMemo(() => filters ? getOfferListKey(filters) : undefined, [filters])
  const offerMetaData = useAppSelector(state => offerListKey ? state.offer.searchResultMetadata.offerMetaData[offerListKey] : undefined)

  const nearbyOfferIds = useMemo(() => {
    const offerIds = without(offerList.offerIds, offerId)
    return offerIds.slice(0, NEARBY_SINGLE_UNIT_LIMIT)
  }, [offerId, offerList.offerIds])

  const offerDistances = useMemo(() => {
    return nearbyOfferIds.reduce<Record<string, number | undefined>>((acc, offerId) => {
      acc[offerId] = offerMetaData?.[offerId]?.distance
      return acc
    }, {})
  }, [nearbyOfferIds, offerMetaData])

  const overrideOfferList = useMemo<App.OfferList>(() => ({
    ...offerList,
    offerIds: nearbyOfferIds,
  }), [nearbyOfferIds, offerList])

  const allLink = useMemo(() => {
    const searchParams = encodeSearchParams({
      searchItem: {
        searchType: 'property',
        value: property.id,
        format: {
          mainText: property.name,
          secondaryText: '',
        },
      },
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms: stableRooms,
      filters: {
        propertyTypes,
      },
    })

    return `${HOMES_AND_VILLAS_SEARCH_PATH}?${searchParams.toString()}`
  }, [checkInDate, checkOutDate, property.id, property.name, propertyTypes, stableRooms])

  if (!filters || (!overrideOfferList.fetching && overrideOfferList.offerIds.length === 0)) {
    return null
  }

  const title = intl.formatMessage({
    id: 'gu0a5XvPRTZXdL1KYPX5',
    defaultMessage: 'Similar properties with availability',
    description: 'Carousel heading for available nearby single-unit properties shown when a property page is unavailable',
  })

  return <OfferRecommendations
    title={title}
    offerId={offerId}
    filters={filters}
    offerDistances={offerDistances}
    offerDistanceFrom={property.name}
    trackingDescription="Unavailable SPP Single Unit"
    overrideOfferList={overrideOfferList}
    maxOffers={NEARBY_SINGLE_UNIT_LIMIT}
    allLink={allLink}
    enableDateCheckingForPriceDisplay
  />
}

export default withOfferPageAndReduxState((state, appState) => ({
  offerId: state.offer?.id ?? state.bedbankOffer?.id,
  property: state.property!, // Guaranteed by `<PropertyContainer/>`.
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  rooms: state.rooms,
  isLoading: arePropertyRatesLoading(state, appState),
  combinedRatesByRoom: getPropertyCombinedRatesByRoom(state, appState),
  propertyTypes: getUrlOfferListFilters(appState).propertyTypes ?? EmptyArray,
  open: !!state.activeFilter,
}), PropertyUnavailableSingleUnitRecommendations, preventRenderWithOpenFilter)
